import {
  Body1,
  Button,
  Caption1,
  Field,
  Input,
  makeStyles,
  Subtitle1,
  tokens,
} from '@fluentui/react-components';
import { PersonRegular } from '@fluentui/react-icons';
import { type FormEvent, useState } from 'react';
import { APP_INFO } from '#src/app/app-info';
import type { DevUser } from '#src/auth/dev-identity';
import { Card, IconTile } from '#src/shared/ui';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100%',
    padding: tokens.spacingHorizontalL,
    boxSizing: 'border-box',
    backgroundImage: `linear-gradient(180deg, ${tokens.colorBrandBackground2} -60%, ${tokens.colorNeutralBackground2} 45%)`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  card: { width: '100%', maxWidth: '420px' },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingHorizontalXXL,
  },
  heading: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  headingText: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  hint: {
    padding: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
  },
  code: { fontFamily: tokens.fontFamilyMonospace },
  subtle: { color: tokens.colorNeutralForeground3 },
});

/**
 * Entrada do modo dev: qualquer e-mail, sem senha. A pessoa é cadastrada no
 * primeiro acesso, sem perfil; as permissões vêm do banco, como em produção.
 */
export function DevSignIn({ onSignIn }: { onSignIn: (user: DevUser) => void }) {
  const styles = useStyles();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = email.trim();
    onSignIn({
      email: trimmed,
      name: name.trim() || (trimmed.split('@')[0] ?? trimmed),
    });
  };

  return (
    <main className={styles.page}>
      <Card className={styles.card}>
        <div className={styles.body}>
          <div className={styles.heading}>
            <IconTile>
              <PersonRegular />
            </IconTile>
            <div className={styles.headingText}>
              <Subtitle1 as="h1">Entrar em {APP_INFO.name}</Subtitle1>
              <Caption1 className={styles.subtle}>
                Modo de desenvolvimento, fora do Teams
              </Caption1>
            </div>
          </div>
          <form className={styles.form} onSubmit={submit}>
            <Field label="E-mail" required>
              <Input
                type="email"
                value={email}
                onChange={(_, data) => setEmail(data.value)}
              />
            </Field>
            <Field label="Nome">
              <Input value={name} onChange={(_, data) => setName(data.value)} />
            </Field>
            <Button appearance="primary" type="submit">
              Entrar
            </Button>
          </form>
          <Body1 className={styles.hint}>
            Sem senha: o backend aceita qualquer e-mail. Para ter acesso a tudo:{' '}
            <span className={styles.code}>
              pnpm env:local backend:cli rbac:grant &lt;e-mail&gt; admin
            </span>
          </Body1>
        </div>
      </Card>
    </main>
  );
}
